// @givmo/crm — the organization's saved segments (CRM-5).
//
// Each segment with how many people are in it now. A count never includes a contact the
// organization may not email, and never a Givmo donor while the consent gate is closed — the page
// then says so in the server's own words.

import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Button,
  Card,
  EmptyState,
  Icon,
  PageHeader,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  testIdProp,
} from '@givmo/design-system';
import { ListFilter, Plus, ShieldAlert, Users } from 'lucide-react';
import type { CrmModuleConfig } from './crm-config';
import { displayGroupedCount, isSliceUnavailable, serverMessage } from './crm-decode';
import { crmSegmentsOptions } from './crm-queries';

const PREFIX = 'crm-segments';

export function CrmSegmentsPage({ config }: { config: CrmModuleConfig }) {
  const navigate = useNavigate();
  const segmentsQuery = useQuery(crmSegmentsOptions(config.orgRef));
  const newSegment = config.links.newSegment;
  const openNew = newSegment === undefined ? undefined : () => void navigate({ to: newSegment });
  const body = segmentsQuery.data;

  return (
    <div
      className="mx-auto flex w-full max-w-[1120px] flex-col gap-16 px-24 py-24"
      {...testIdProp('page-segments')}
    >
      <PageHeader
        title="Segments"
        subtitle="Saved groups of your contacts. Each one updates itself as your contacts change."
        organizationName={config.orgName}
        organizationNameTestId={`${PREFIX}-org-badge`}
        actions={
          openNew === undefined ? undefined : (
            <Button
              appearance="saas"
              tone="primary"
              size="sm"
              leadingIcon={<Icon icon={Plus} size={16} aria-hidden />}
              onClick={openNew}
              testId={`${PREFIX}-new`}
            >
              New segment
            </Button>
          )
        }
      />

      {body !== undefined && !body.consent_available && body.message ? (
        <p
          className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary"
          {...testIdProp(`${PREFIX}-consent-note`)}
        >
          {body.message}
        </p>
      ) : null}

      {segmentsQuery.isPending ? (
        <div className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-loading`)}>
          <Skeleton className="h-12 w-1/3" />
          <Skeleton className="h-12 w-2/3" />
        </div>
      ) : segmentsQuery.isError && isSliceUnavailable(segmentsQuery.error) ? (
        <EmptyState
          testId={`${PREFIX}-unavailable`}
          icon={<Icon icon={Users} size={20} aria-hidden />}
          title="Not available yet"
          description="This part of the portal isn’t switched on for your organization yet."
        />
      ) : segmentsQuery.isError ? (
        <EmptyState
          testId={`${PREFIX}-error`}
          icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
          title="Couldn’t load your segments"
          description={serverMessage(
            segmentsQuery.error,
            'We couldn’t load your segments. Please try again.',
          )}
          action={
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void segmentsQuery.refetch()}
              testId={`${PREFIX}-retry`}
            >
              Try again
            </Button>
          }
        />
      ) : body === undefined || body.segments.length === 0 ? (
        <EmptyState
          testId={`${PREFIX}-empty`}
          icon={<Icon icon={ListFilter} size={20} aria-hidden />}
          title="No segments yet"
          description="A segment is a group of your contacts, such as everyone tagged Donor."
          action={
            openNew === undefined ? undefined : (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={openNew}
                testId={`${PREFIX}-empty-new`}
              >
                New segment
              </Button>
            )
          }
        />
      ) : (
        <Card className="overflow-hidden" {...testIdProp(`${PREFIX}-list`)}>
          <Table>
            <THead>
              <tr>
                <TH>Name</TH>
                <TH align="right">People</TH>
              </tr>
            </THead>
            <TBody>
              {body.segments.map((segment) => (
                <TR key={segment.id} testId={`${PREFIX}-row-${segment.id}`}>
                  <TD>
                    {config.links.segment === undefined ? (
                      <span className="font-w600 text-text-strong">{segment.name}</span>
                    ) : (
                      <Link
                        to={config.links.segment(segment.id)}
                        className="rounded-sm font-w600 text-text-strong outline-none hover:underline focus-visible:shadow-ring"
                      >
                        {segment.name}
                      </Link>
                    )}
                  </TD>
                  <TD align="right" numeric>
                    <span className="text-text-secondary tabular-nums">
                      {displayGroupedCount(segment.count)}
                    </span>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
